(() => {
  'use strict';
  const route = (path) => (window.Shopify?.routes?.root || '/') + path;
  const reference = (item) => item.properties?.['_Bundle group'] || item.properties?.['_Bundle reference'] || item.properties?.['Bundle reference'];
  const fingerprint = (item) => JSON.stringify([String(item.variant_id), item.quantity, Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);
  const eligible = (item) => item?.quantity === 1 && item.properties?.['_Bundle role'] === 'configured bundle';
  const fieldName = (key) => 'properties[' + key + ']';
  let busy = false;
  const request = async (path, body) => {
    const response = await fetch(route(path), {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      ...(body ? { body: body instanceof FormData ? body : JSON.stringify(body) } : {})
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Cart request failed');
    return result;
  };
  const compact = (source) => {
    const result = {};
    const internal = new Set(['Bundle reference', 'Bundle line', 'Quantity-change instructions', 'Base package price', 'Configured merchandise subtotal', 'Order contact']);
    for (const [key, value] of Object.entries(source)) {
      if (value == null || value === '') continue;
      const zeroSize = /^(Small|Medium|Large|XL|[2-5]XL) quantity$/.test(key) && Number(value) === 0;
      const zeroAddition = /^(?:[2-5]XL addition|Extended-size additions)$/.test(key) && (/^0 x /.test(String(value)) || !/[1-9]/.test(String(value)));
      const target = !key.startsWith('_') && (internal.has(key) || zeroSize || zeroAddition) ? '_' + key : key;
      result[target] = String(value);
    }
    return result;
  };
  const restore = async (root, messages) => {
    const key = new URLSearchParams(location.search).get('lfg_edit');
    if (!key) return null;
    const cart = await request('cart.js');
    const item = cart.items.find((line) => line.key === key);
    const productPath = new URL(item?.url || '/', location.origin).pathname;
    if (!eligible(item) || productPath !== location.pathname) throw new Error(messages.editUnavailable);
    const pending = cart.items.filter((line) => line.properties?.['_LFG replaces bundle key'] === key);
    if (pending.length > 1) throw new Error(messages.reviewCart);
    const properties = (pending[0] || item).properties || {};
    root.querySelectorAll('[name^="properties["]').forEach((field) => {
      const name = field.name.slice(11, -1);
      const value = properties[name] ?? properties['_' + name];
      if (value == null || field.type === 'file') return;
      if (field.type === 'checkbox' || field.type === 'radio') field.checked = field.value === String(value);
      else field.value = String(value);
    });
    const file = properties['Artwork file'];
    if (file) {
      const url = new URL(file, location.origin);
      if (url.protocol === 'https:' && /\/uploads\//.test(url.pathname)) {
        const notice = document.createElement('p');
        notice.textContent = messages.artworkRetained + ' ';
        const link = document.createElement('a');
        link.href = url.href; link.textContent = messages.viewArtwork; link.target = '_blank'; link.rel = 'noopener';
        notice.append(link); root.querySelector('input[type="file"]')?.after(notice);
      }
    }
    return { key, fingerprint: fingerprint(item), item, pendingReference: pending[0] && reference(pending[0]) };
  };
  const save = async ({ state, properties, edit, messages }) => {
    if (busy) throw new Error(messages.saving);
    busy = true;
    const perform = async () => {
      const intent = history.state?.lfgPurchaseIntent || crypto.randomUUID();
      history.replaceState({ ...history.state, lfgPurchaseIntent: intent }, '', location.href);
      const storageKey = 'lfg-config-save:' + location.pathname + ':' + (edit?.key || intent);
      let record;
      try { record = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { throw new Error(messages.storageUnavailable); }
      let cart = await request('cart.js');
      if (record?.signature !== state.signature) {
        if (record && cart.items.some((item) => reference(item) === record.reference)) throw new Error(messages.reviewCart);
        record = null;
      }
      const old = edit && cart.items.find((item) => item.key === edit.key);
      if (edit && old && fingerprint(old) !== edit.fingerprint) throw new Error(messages.cartChanged);
      const found = record && cart.items.filter((item) => reference(item) === record.reference);
      if (edit && !old && !(found?.length === 1)) throw new Error(messages.cartChanged);
      if (!record) {
        record = { reference: edit?.pendingReference || 'LFG-' + crypto.randomUUID(), signature: state.signature, attempted: !!edit?.pendingReference };
        try { sessionStorage.setItem(storageKey, JSON.stringify(record)); } catch { throw new Error(messages.storageUnavailable); }
      }
      const source = { ...(edit?.item.properties || {}) };
      for (const [name, value] of new FormData(state.formElement)) {
        if (!name.startsWith('properties[') || value instanceof File) continue;
        const key = name.slice(11, -1);
        delete source[key]; delete source['_' + key];
      }
      Object.assign(source, properties);
      for (const key of Object.keys(properties)) delete source['_' + key];
      source['Bundle reference'] = record.reference;
      source['_Bundle group'] = record.reference;
      source['Bundle line'] = 'Single configured LF Graphco bundle';
      source['Quantity-change instructions'] = 'Use Edit configuration in the cart';
      source['_Bundle role'] = 'configured bundle';
      source['_Extended-size addition cents'] = String(state.additions);
      source['_Configured merchandise cents'] = String(state.configuredCents);
      source['_LFG configuration version'] = '2026-10-31';
      if (edit) source['_LFG replaces bundle key'] = edit.key;
      const finalProperties = compact(source);
      const matches = () => cart.items.filter((item) => reference(item) === record.reference);
      let added = matches();
      if (!added.length) {
        if (record.attempted) throw new Error(messages.reviewCart);
        record.attempted = true;
        sessionStorage.setItem(storageKey, JSON.stringify(record));
        const body = new FormData();
        body.set('id', String(state.variantId)); body.set('quantity', '1');
        Object.entries(finalProperties).forEach(([key, value]) => body.set(fieldName(key), value));
        for (const [key, value] of new FormData(state.formElement)) {
          if (key.startsWith('properties[') && value instanceof File && value.size) body.set(key, value, value.name);
        }
        try { await request('cart/add.js', body); } catch { }
        cart = await request('cart.js'); added = matches();
        if (!added.length) throw new Error(messages.reviewCart);
      }
      if (added.length !== 1 || added[0].quantity !== 1 || String(added[0].variant_id) !== String(state.variantId) || added[0].original_price !== state.configuredCents) throw new Error(messages.reviewCart);
      if (Object.entries(finalProperties).some(([key, value]) => key !== 'Artwork file' && added[0].properties[key] !== value)) throw new Error(messages.reviewCart);
      if (edit) {
        const currentOld = cart.items.find((item) => item.key === edit.key);
        if (currentOld) {
          if (fingerprint(currentOld) !== edit.fingerprint) throw new Error(messages.cartChanged);
          try { await request('cart/change.js', { id: edit.key, quantity: 0 }); } catch { }
          cart = await request('cart.js');
          if (cart.items.some((item) => item.key === edit.key)) throw new Error(messages.finishSave);
        }
      }
      const final = cart.items.filter((item) => reference(item) === record.reference);
      if (final.length !== 1 || final[0].quantity !== 1) throw new Error(messages.reviewCart);
      record.complete = true; sessionStorage.setItem(storageKey, JSON.stringify(record));
      return cart;
    };
    try {
      return await (navigator.locks ? navigator.locks.request('lfg-config-cart', perform) : perform());
    } finally { busy = false; }
  };
  window.LfgBundleCart = { restore, save, compact };
})();
